/**
 * Firebase Authentication Service
 * 
 * Handles anonymous authentication for submission tracking.
 * Each device gets a unique UID to prevent duplicate submissions.
 */

import { signInAnonymously, signOut } from 'firebase/auth';
import { auth } from './firebase';

/**
 * Sign in anonymously
 * Creates a new anonymous session or resumes existing one
 * @returns {Promise<string>} User UID
 */
export const loginAnonymously = async () => {
  try {
    const userCredential = await signInAnonymously(auth);
    const uid = userCredential.user.uid;
    console.log('Anonymous login successful. UID:', uid);
    return uid;
  } catch (error) {
    console.error('Anonymous login failed:', error);
    throw error;
  }
};

/**
 * Sign out current user
 * Used when user wants to make another submission
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
    console.log('User logged out');
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

/**
 * Get current user's UID
 * @returns {string|null} Current user UID or null if not authenticated
 */
export const getCurrentUID = () => {
  return auth.currentUser?.uid || null;
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return auth.currentUser !== null;
};

/**
 * Listen to auth state changes
 * @param {Function} callback - Called when auth state changes
 * @returns {Function} Unsubscribe function
 */
export const onAuthChange = (callback) => {
  return auth.onAuthStateChanged(callback);
};
